import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { Link, useNavigate, useParams } from 'react-router-dom';
import AppSidebar from './AppSidebar';
import { Navbar } from './common/Catalyst/navbar';
import { SidebarLayout } from './common/Catalyst/sidebar-layout';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from './common/Catalyst/dialog';
import { Button } from './common/Catalyst/button';
import LoadingSpinner from './LoadingSpinner';
import { amenityService, hoaService } from './clients/ApiClients';
import { AmenityBookingMode, AmenityReturn } from './clients/rust-api-client';
import {
  canManageAmenities,
  canManageReservationPolicy,
} from './constants/UserTypes';
import { getUserTypeFromToken } from './utils/TokenUtils';
import { formatMoney } from './utils/billingHelpers';
import { useSyncedHoa } from './sync/hooks';
import { ReservationPolicyPanel } from './amenity/ReservationPolicyPanel';
import { AmenityGroupsSection } from './amenity/AmenityGroupsSection';
import {
  APPROVAL_MODE_LABELS,
  BOOKING_MODE_LABELS,
  BOOKING_MODES,
  formatWindows,
  reservationErrorMessage,
  todayIn,
  weekdayOf,
  windowsFor,
} from './reservation/reservationMeta';
import { inputClass, labelClass } from './reservation/formStyles';
import { ErrorAlert } from './common/Custom/ErrorAlert';
import { useSyncedAmenities } from './sync/reservationHooks';

/**
 * An association's amenities (#642): what it offers, how each books, and
 * — for an Owner or Admin — the reservation policy that applies to all of
 * them. Each row opens the amenity's setup page.
 */
const AmenitiesPage: React.FC = () => {
  const { hoaId } = useParams<{ hoaId: string }>();
  const navigate = useNavigate();
  const userType = getUserTypeFromToken();
  const canSetUp = canManageAmenities(userType);
  const canPolicy = canManageReservationPolicy(userType);
  const syncedHoa = useSyncedHoa(hoaId);
  const [hoaName, setHoaName] = useState<string | null>(null);
  const [fetchedAmenities, setAmenities] = useState<AmenityReturn[]>([]);
  const [showArchived, setShowArchived] = useState(false);
  const [fetching, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  // Bumped when an amenity group changes, so each amenity's group shows.
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    if (!hoaId || syncedHoa) return;
    hoaService
      .getHoa({ id: hoaId })
      .then((r) => setHoaName(r.data.name))
      .catch(() => undefined);
  }, [hoaId, syncedHoa]);

  // From the sync engine's copy when there is one (#561), with hours,
  // seasons and group names joined in; a group renamed elsewhere shows
  // here without a refresh.
  const synced = useSyncedAmenities({
    hoaId,
    includeInactive: true,
    includeArchived: showArchived,
  });
  const haveSynced = synced !== null;
  const amenities = synced ?? fetchedAmenities;
  const loading = !haveSynced && fetching;

  useEffect(() => {
    if (!hoaId || haveSynced) return;
    let live = true;
    setLoading(true);
    amenityService
      .listAmenities({ hoaId, includeInactive: true, includeArchived: showArchived })
      .then((r) =>
        live &&
        setAmenities(
          [...r.data.amenities].sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name))
        )
      )
      .catch((err) => live && setError(reservationErrorMessage(err, "Couldn't load amenities.")))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [hoaId, showArchived, refresh, haveSynced]);

  const name = syncedHoa?.name ?? hoaName;

  return (
    <SidebarLayout
      sidebar={<AppSidebar currentPage="reservations" />}
      navbar={<Navbar>{/* Your navbar content */}</Navbar>}
    >
      <div className="-mx-2 mb-6 flex items-center gap-2 border-b border-line/60 px-2 pb-3 text-[13px]">
        <Link to={`/hoa/${hoaId}`} className="text-fg-muted hover:text-fg">
          {name ?? 'Association'}
        </Link>
        <span className="text-fg-faint" aria-hidden>
          /
        </span>
        <span className="font-medium text-fg">Amenities</span>
        <div className="ml-auto flex items-center gap-3">
          <Link to={`/hoa/${hoaId}/reservations`} className="text-fg-muted hover:text-fg">
            Reservations
          </Link>
          {canSetUp && (
            <Button color="sky" onClick={() => setCreating(true)}>
              New amenity
            </Button>
          )}
        </div>
      </div>

      <ErrorAlert>{error}</ErrorAlert>

      {loading ? (
        <LoadingSpinner />
      ) : amenities.length === 0 ? (
        <div className="rounded-lg border border-dashed border-line px-6 py-10 text-center">
          <p className="text-[15px] font-medium text-fg">No amenities yet</p>
          <p className="mt-1 text-[13px] text-fg-muted">
            Add the clubhouse, courts, pool or anything else households can use — bookable or just listed with its
            hours and rules.
          </p>
        </div>
      ) : (
        <ul className="divide-y divide-line/60 overflow-hidden rounded-lg border border-line bg-raised">
          {amenities.map((a) => {
            const today = todayIn(a.timezone);
            const hours = formatWindows(windowsFor(a.hours, weekdayOf(today)));
            return (
              <li key={a.id}>
                <button
                  type="button"
                  onClick={() => navigate(`/hoa/${hoaId}/amenities/${a.id}`)}
                  className="flex w-full items-center gap-4 px-4 py-3 text-left text-[13px] hover:bg-fg/[0.03]"
                >
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span className="font-medium text-fg">{a.name}</span>
                      {!a.is_active && (
                        <span className="rounded-full border border-line px-2 py-0.5 text-[11px] text-fg-muted">
                          {a.archived_at ? 'Archived' : 'Inactive'}
                        </span>
                      )}
                    </span>
                    <span className="block truncate text-[12px] text-fg-muted">
                      {BOOKING_MODE_LABELS[a.booking_mode] ?? a.booking_mode}
                      {a.booking_mode !== 'info_only' && ` · ${APPROVAL_MODE_LABELS[a.approval_mode]}`}
                      {a.group_name ? ` · ${a.group_name}` : ''}
                      {a.location ? ` · ${a.location}` : ''}
                    </span>
                  </span>
                  <span className="hidden text-[12px] text-fg-muted sm:block">Today: {hours}</span>
                  <span className="w-24 text-right text-[12px] text-fg-secondary">
                    {a.fee_type === 'none' || !a.fee_amount
                      ? 'Free'
                      : `${formatMoney(a.fee_amount)}${a.fee_type === 'hourly' ? '/hr' : a.fee_type === 'per_night' ? '/night' : ''}`}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <label className="mt-3 flex items-center gap-2 text-[12px] text-fg-muted">
        <input
          type="checkbox"
          checked={showArchived}
          onChange={(e) => setShowArchived(e.target.checked)}
          className="h-3.5 w-3.5 rounded border-line-strong bg-transparent text-accent focus:ring-accent"
        />
        Show archived
      </label>

      {hoaId && !loading && (
        <div className="mt-10 max-w-3xl">
          <AmenityGroupsSection
            hoaId={hoaId}
            amenities={amenities}
            readOnly={!canSetUp}
            onChanged={() => setRefresh((n) => n + 1)}
          />
        </div>
      )}

      {canPolicy && hoaId && (
        <div className="mt-10 max-w-2xl">
          <ReservationPolicyPanel hoaId={hoaId} />
        </div>
      )}

      {hoaId && (
        <NewAmenityDialog
          open={creating}
          hoaId={hoaId}
          onClose={() => setCreating(false)}
          onCreated={(a) => navigate(`/hoa/${hoaId}/amenities/${a.id}`)}
        />
      )}
    </SidebarLayout>
  );
};

/** Name and kind; everything else is set on the amenity's own page. */
const NewAmenityDialog: React.FC<{
  open: boolean;
  hoaId: string;
  onClose: () => void;
  onCreated: (a: AmenityReturn) => void;
}> = ({ open, hoaId, onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [mode, setMode] = useState<AmenityBookingMode>('exclusive');
  const [capacity, setCapacity] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName('');
      setMode('exclusive');
      setCapacity('');
      setError(null);
    }
  }, [open]);

  // A shared amenity needs its headcount from the start; a stay needs
  // check-in and check-out times (3 PM / 11 AM, changeable afterwards).
  const capacityOk = mode !== 'shared_capacity' || (/^\d+$/.test(capacity) && Number(capacity) > 0);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !capacityOk) return;
    setSaving(true);
    setError(null);
    try {
      const res = await amenityService.createAmenity({
        amenityCreate: {
          hoa_id: hoaId,
          name: name.trim(),
          booking_mode: mode,
          ...(mode === 'shared_capacity' ? { capacity: Number(capacity) } : {}),
          ...(mode === 'overnight' ? { check_in_minute: 15 * 60, check_out_minute: 11 * 60 } : {}),
        },
      });
      onCreated(res.data);
    } catch (err) {
      setError(reservationErrorMessage(err, "Couldn't create the amenity."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} size="md">
      <form onSubmit={submit}>
        <DialogTitle>New amenity</DialogTitle>
        <DialogBody className="space-y-4">
          <label className={labelClass}>
            Name
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Clubhouse"
              className={inputClass}
            />
          </label>
          <fieldset className="space-y-2">
            <legend className="text-[12px] font-medium text-fg-muted">Kind</legend>
            {BOOKING_MODES.map((m) => (
              <label key={m} className="flex items-center gap-2 text-[13px] text-fg-secondary">
                <input
                  type="radio"
                  name="booking-mode"
                  checked={mode === m}
                  onChange={() => setMode(m)}
                  className="h-4 w-4 border-line-strong bg-transparent text-accent focus:ring-accent"
                />
                {BOOKING_MODE_LABELS[m]}
              </label>
            ))}
          </fieldset>
          {mode === 'shared_capacity' && (
            <label className={labelClass}>
              Headcount per time slot
              <input
                inputMode="numeric"
                value={capacity}
                onChange={(e) => setCapacity(e.target.value.replace(/\D/g, ''))}
                placeholder="e.g. 20"
                className={clsx(inputClass, 'w-28', capacity && !capacityOk && 'border-danger')}
              />
            </label>
          )}
          {mode === 'overnight' && (
            <p className="text-[12px] text-fg-muted">Starts with check-in at 3 PM and check-out at 11 AM.</p>
          )}
          <p className="text-[12px] text-fg-muted">
            Set its hours, booking rules and fee on the next page. Households cannot book it until it has opening hours.
          </p>
          <ErrorAlert>{error}</ErrorAlert>
        </DialogBody>
        <DialogActions>
          <Button plain type="button" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button color="sky" type="submit" disabled={saving || !name.trim() || !capacityOk}>
            {saving ? 'Creating…' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AmenitiesPage;
